/**
 * catalogOperator — operator-grade role check for the admin catalogue surface.
 *
 * policy-2026-08-01-tenant-admin-never-creates-apps: app creation, deployment
 * rollout and catalogue mutation are exclusively developer and operator verbs.
 * The tenant-admin role is request-and-govern only (sandbox requests, assign
 * the uat-tester, personas in the app realm) and must never see or reach the
 * admin tile actions (Admin / Deploy / Edit description).
 *
 * This is the SPA half (defense in depth — hiding, not the gate). The gate is
 * server-side: the service-broker registry mutations require
 * REGISTRY_DEVELOPER_ROLES / REGISTRY_ADMIN_ROLES, neither of which includes
 * tenant-admin (see infraops registry-tenant-admin-forbidden.test.ts).
 *
 * 2026-08-02: broker-operator removed —
 * open-task-2026-08-02-broker-operator-role-absent-in-realm resolved STALE (it
 * existed in no realm and as no BrokerRole node, so it had never matched
 * anybody).
 *
 * 2026-08-03 (decision-2026-08-03-role-model-planes): service-admin and
 * vertical-product-owner are retired as names; the catalogue operator is the
 * broker's administrator, `broker-admin`, per role-model proposal §3.
 *
 * ⚠ VITE_CATALOG_OPERATOR_ROLES IS A **BUILD-TIME** OVERRIDE, NOT A RUNTIME ONE.
 * `import.meta.env` is substituted by Vite when the bundle is built, so setting
 * the variable on a running container changes NOTHING — the value is already
 * baked into the shipped JS. THE CODE DEFAULT BELOW IS THEREFORE THE REAL
 * CONTROL for every deployment that does not rebuild, and it must always carry
 * the model we actually want enforced. (The server-side lists in infraops ARE
 * read at runtime from process.env; that asymmetry is the trap this note exists
 * to close.)
 *
 * NOTE the deliberate asymmetry with the server's CATALOG_ADMIN_ROLES
 * (services.ts), which also includes tenant-admin — that list governs
 * private-tile VISIBILITY; this list governs mutation AFFORDANCES. Seeing is
 * not touching.
 */

const raw = (import.meta.env?.VITE_CATALOG_OPERATOR_ROLES as string | undefined) ?? '';

export const CATALOG_OPERATOR_ROLES: readonly string[] =
  raw.split(',').map((s) => s.trim()).filter(Boolean).length > 0
    ? raw.split(',').map((s) => s.trim()).filter(Boolean)
    : ['broker-admin'];

/** True when the token carries at least one operator-grade catalogue role. */
export function isCatalogOperator(roles: readonly string[]): boolean {
  return roles.some((r) => CATALOG_OPERATOR_ROLES.includes(r));
}
